import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  CreditCard as CardIcon, 
  Package, 
  Truck,
  CheckCircle2,
  Palette,
  QrCode
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CARD_DESIGNS = [
  { id: 'professional', name: 'Professional', description: 'Classic business look with gradient background' },
  { id: 'modern', name: 'Modern', description: 'Contemporary design with decorative elements' },
  { id: 'minimal', name: 'Minimal', description: 'Clean and simple with accent bar' },
];

const CARD_COLORS = [
  { id: 'classic', name: 'Classic Navy', color: '#16335E' },
  { id: 'ocean', name: 'Ocean Blue', color: '#0077b6' },
  { id: 'forest', name: 'Forest Green', color: '#2d6a4f' },
  { id: 'sunset', name: 'Sunset Red', color: '#e63946' },
  { id: 'royal', name: 'Royal Purple', color: '#7209b7' },
  { id: 'minimal', name: 'White/Light', color: '#f8f9fa' },
  { id: 'dark', name: 'Dark Mode', color: '#212529' },
  { id: 'rose', name: 'Rose Gold', color: '#b76e79' },
];

const OrderPage = () => {
  const { cardId } = useParams();
  const [searchParams] = useSearchParams();
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cardData, setCardData] = useState(null);
  const [step, setStep] = useState(1);
  const [orderSuccess, setOrderSuccess] = useState(false);
  
  const [orderForm, setOrderForm] = useState({
    quantity: 1,
    card_design: searchParams.get('design') || 'professional',
    card_color: searchParams.get('color') || 'classic',
    include_qr: true,
    shipping_name: '',
    shipping_email: '',
    shipping_phone: '',
    shipping_address: '',
    shipping_city: '',
    shipping_state: '',
    shipping_zip: '',
    shipping_country: 'USA',
    notes: ''
  });

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/cards/${cardId}`, { headers: getAuthHeaders() });
        setCardData(response.data);
        // Pre-fill shipping details from user if available
        setOrderForm(prev => ({
          ...prev,
          shipping_name: response.data.name || user?.name || '',
          shipping_email: response.data.emails?.[0]?.value || response.data.email || user?.email || '',
          shipping_phone: response.data.phones?.[0]?.value || response.data.phone || ''
        }));
      } catch (error) {
        toast.error('Card not found');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
  }, [cardId]);

  const handleSubmit = async () => {
    // Validate form
    const required = ['shipping_name', 'shipping_email', 'shipping_phone', 'shipping_address', 'shipping_city', 'shipping_state', 'shipping_zip'];
    const missing = required.filter(field => !orderForm[field]);
    
    if (missing.length > 0) {
      toast.error('Please fill in all required shipping fields');
      setStep(2);
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/orders`, {
        card_id: cardId,
        ...orderForm
      }, { headers: getAuthHeaders() });
      
      setOrderSuccess(true);
      toast.success('Order placed successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  const updateForm = (field, value) => {
    setOrderForm(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="loading-spinner" />
        </div>
      </Layout>
    );
  }

  if (orderSuccess) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto text-center py-16">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold mb-4">Order Placed Successfully!</h1>
          <p className="text-muted-foreground mb-8">
            Thank you for your order. We've sent a confirmation to {orderForm.shipping_email}. 
            Our team will process your order and reach out with shipping details.
          </p>
          <div className="flex gap-4 justify-center">
            <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="back-dashboard-btn">
              Back to Dashboard
            </Button>
            <Button onClick={() => navigate('/orders')} data-testid="view-orders-btn">
              View Orders
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/cards/${cardId}/edit`)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold tracking-tight">Order Physical Card</h1>
            <p className="text-muted-foreground">Get your NFC-enabled business card printed</p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 py-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                  step >= s ? 'bg-[#16335E] text-white' : 'bg-muted text-muted-foreground'
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div className={`w-16 h-1 mx-2 rounded ${step > s ? 'bg-[#16335E]' : 'bg-muted'}`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-center gap-12 text-sm text-muted-foreground mb-6">
          <span className={step >= 1 ? 'text-[#16335E] font-medium' : ''}>Design</span>
          <span className={step >= 2 ? 'text-[#16335E] font-medium' : ''}>Shipping</span>
          <span className={step >= 3 ? 'text-[#16335E] font-medium' : ''}>Review</span>
        </div>

        {/* Step 1: Design Options */}
        {step === 1 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Card Design
                </CardTitle>
                <CardDescription>Choose the design template for your physical card</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {CARD_DESIGNS.map((design) => (
                    <button
                      key={design.id}
                      onClick={() => updateForm('card_design', design.id)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        orderForm.card_design === design.id 
                          ? 'border-[#16335E] bg-[#16335E]/5' 
                          : 'border-muted hover:border-muted-foreground/30'
                      }`}
                      data-testid={`design-${design.id}`}
                    >
                      <h4 className="font-semibold">{design.name}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{design.description}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Color Scheme</CardTitle>
                <CardDescription>Select the primary color for your card</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
                  {CARD_COLORS.map((color) => (
                    <button
                      key={color.id}
                      onClick={() => updateForm('card_color', color.id)}
                      className={`aspect-square rounded-lg transition-all relative ${
                        orderForm.card_color === color.id 
                          ? 'ring-2 ring-[#16335E] ring-offset-2' 
                          : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color.color }}
                      title={color.name}
                      data-testid={`color-${color.id}`}
                    />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mt-3 text-center">
                  {CARD_COLORS.find(c => c.id === orderForm.card_color)?.name}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <QrCode className="w-5 h-5" />
                  Options
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Include QR Code</Label>
                    <p className="text-sm text-muted-foreground">Print a QR code on the back of the card</p>
                  </div>
                  <Switch
                    checked={orderForm.include_qr}
                    onCheckedChange={(v) => updateForm('include_qr', v)}
                    data-testid="include-qr-switch"
                  />
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Select value={String(orderForm.quantity)} onValueChange={(v) => updateForm('quantity', parseInt(v))}>
                    <SelectTrigger data-testid="quantity-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 5, 10, 25, 50, 100].map((qty) => (
                        <SelectItem key={qty} value={String(qty)}>{qty} card{qty > 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} className="gap-2" data-testid="next-step-btn">
                Continue to Shipping
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Shipping Details */}
        {step === 2 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Shipping Information
                </CardTitle>
                <CardDescription>Where should we send your cards?</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="shipping_name">Full Name *</Label>
                    <Input
                      id="shipping_name"
                      value={orderForm.shipping_name}
                      onChange={(e) => updateForm('shipping_name', e.target.value)}
                      placeholder="John Doe"
                      data-testid="shipping-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shipping_email">Email *</Label>
                    <Input
                      id="shipping_email"
                      type="email"
                      value={orderForm.shipping_email}
                      onChange={(e) => updateForm('shipping_email', e.target.value)}
                      placeholder="john@example.com"
                      data-testid="shipping-email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="shipping_phone">Phone Number *</Label>
                  <Input
                    id="shipping_phone"
                    value={orderForm.shipping_phone}
                    onChange={(e) => updateForm('shipping_phone', e.target.value)}
                    placeholder="+1 555-123-4567"
                    data-testid="shipping-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="shipping_address">Street Address *</Label>
                  <Input
                    id="shipping_address"
                    value={orderForm.shipping_address}
                    onChange={(e) => updateForm('shipping_address', e.target.value)}
                    placeholder="123 Main Street, Apt 4"
                    data-testid="shipping-address-input"
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="shipping_city">City *</Label>
                    <Input
                      id="shipping_city"
                      value={orderForm.shipping_city}
                      onChange={(e) => updateForm('shipping_city', e.target.value)}
                      placeholder="New York"
                      data-testid="shipping-city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shipping_state">State *</Label>
                    <Input
                      id="shipping_state"
                      value={orderForm.shipping_state}
                      onChange={(e) => updateForm('shipping_state', e.target.value)}
                      placeholder="NY"
                      data-testid="shipping-state-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shipping_zip">ZIP Code *</Label>
                    <Input
                      id="shipping_zip"
                      value={orderForm.shipping_zip}
                      onChange={(e) => updateForm('shipping_zip', e.target.value)}
                      placeholder="10001"
                      data-testid="shipping-zip-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shipping_country">Country *</Label>
                    <Input
                      id="shipping_country"
                      value={orderForm.shipping_country}
                      onChange={(e) => updateForm('shipping_country', e.target.value)}
                      placeholder="USA"
                      data-testid="shipping-country-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Order Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    value={orderForm.notes}
                    onChange={(e) => updateForm('notes', e.target.value)}
                    placeholder="Any special instructions for your order..."
                    rows={3}
                    data-testid="order-notes-input"
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="prev-step-btn">
                Back
              </Button>
              <Button onClick={() => setStep(3)} className="gap-2" data-testid="next-step-btn">
                Review Order
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Order Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Card Info */}
                <div className="p-4 bg-muted/50 rounded-xl">
                  <h4 className="font-semibold mb-2">Card Details</h4>
                  <p className="text-sm"><strong>Card Name:</strong> {cardData?.name}</p>
                  <p className="text-sm"><strong>Design:</strong> {CARD_DESIGNS.find(d => d.id === orderForm.card_design)?.name}</p>
                  <p className="text-sm"><strong>Color:</strong> {CARD_COLORS.find(c => c.id === orderForm.card_color)?.name}</p>
                  <p className="text-sm"><strong>Quantity:</strong> {orderForm.quantity}</p>
                  <p className="text-sm"><strong>QR Code:</strong> {orderForm.include_qr ? 'Yes' : 'No'}</p>
                </div>

                {/* Shipping Info */}
                <div className="p-4 bg-muted/50 rounded-xl">
                  <h4 className="font-semibold mb-2">Shipping To</h4>
                  <p className="text-sm">{orderForm.shipping_name}</p>
                  <p className="text-sm">{orderForm.shipping_address}</p>
                  <p className="text-sm">{orderForm.shipping_city}, {orderForm.shipping_state} {orderForm.shipping_zip}</p>
                  <p className="text-sm">{orderForm.shipping_country}</p>
                  <p className="text-sm mt-2">{orderForm.shipping_email}</p>
                  <p className="text-sm">{orderForm.shipping_phone}</p>
                </div>

                {orderForm.notes && (
                  <div className="p-4 bg-muted/50 rounded-xl">
                    <h4 className="font-semibold mb-2">Notes</h4>
                    <p className="text-sm text-muted-foreground">{orderForm.notes}</p>
                  </div>
                )}

                <div className="p-4 bg-[#16335E]/5 border border-[#16335E]/20 rounded-xl">
                  <p className="text-sm text-muted-foreground">
                    By placing this order, our team will review your card design and contact you 
                    with pricing and shipping details. Payment will be collected separately.
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="prev-step-btn">
                Back
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={submitting}
                className="gap-2 bg-[#16335E] hover:bg-[#0A1A34]"
                data-testid="place-order-btn"
              >
                {submitting ? (
                  <>
                    <div className="loading-spinner w-4 h-4" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CardIcon className="w-4 h-4" />
                    Place Order
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OrderPage;
